import type { Habit, HabitCompletion, HabitInterval, HabitSlot } from '../types';
import { historyOn, resolveSlot, slotOfCompletion, slotsOf, slotsOn } from './habitSlots';

/**
 * Per-completion XP weight by interval — rarer commitments are worth more
 * (a yearly habit completion = 8 daily ones). Multiplies the base habit points
 * alongside the level + streak factors.
 */
export const intervalWeight = (interval: HabitInterval): number =>
  interval === 'yearly' ? 8 : interval === 'monthly' ? 4 : interval === 'weekly' ? 2 : 1;

/**
 * Helpers for "times per day" habits (e.g. 5 prayers, 8 glasses of water).
 * A habit with targetPerDay > 1 is tracked by COUNT: each completion is its own
 * record (and its own XP), and the day counts toward the streak only once the
 * count reaches the target.
 *
 * Labelled habits (intervalLabels set, e.g. Fajr…Isha) track DISTINCT named
 * slots instead of an anonymous tally: filling the same slot twice is a no-op,
 * and the day's count is the number of distinct slots done. ⭐ Slots are matched by
 * their permanent ID (phase 3b-1, `utils/habitSlots.ts`), and only the habit's
 * CURRENT slots count — a renamed slot keeps its completions, a removed one's are
 * history, and two slots may share a name.
 *
 * The one-time "all intervals done today" bonus is stored as a completion with
 * `bonus: true` — it contributes XP but is excluded from count/streak math.
 */

/**
 * Named interval slots ({ id, label }) for a labelled habit, or null for an anonymous count.
 * ⭐ With a `date`: the intervals it had THAT day (`slotsOn` — the eating habit's change with
 * its meal plan, 2026-10-18). Without: today's (its current ones).
 */
export const habitSlots = (h: Habit, date?: string): HabitSlot[] | null => (date ? slotsOn(h, date) : slotsOf(h));

/** True when the habit tracks distinct named slots rather than a flat tally. */
export const isLabeledHabit = (h: Habit, date?: string): boolean => habitSlots(h, date) !== null;

/** Daily target (>= 1). Labelled habits derive it from their slot count — on `date`, the count it had then. */
export const habitTarget = (h: Habit, date?: string): number => {
  const slots = habitSlots(h, date);
  if (slots) return slots.length;
  const past = date ? historyOn(h, date) : null;
  return Math.max(1, Math.floor((past ? past.target : h.targetPerDay) ?? 1));
};

/** True when this habit is tracked by count rather than a single toggle. */
export const isCountHabit = (h: Habit): boolean => habitTarget(h) > 1;

/** Real (non-bonus) completions for a date. */
const realCompletionsForDate = (h: Habit, date: string): HabitCompletion[] =>
  (h.completions ?? []).filter((c) => c.date === date && !c.bonus);

/** IDs of the slots the habit had on a date that are filled for it (labelled habits). */
export const completedSlotsForDate = (h: Habit, date: string): Set<string> => {
  const slots = habitSlots(h, date);
  if (!slots) return new Set();
  const done = new Set<string>();
  for (const c of realCompletionsForDate(h, date)) {
    const s = slotOfCompletion(slots, c);
    if (s) done.add(s.id);
  }
  return done;
};

/** Whether a specific slot is done for the date — `ref` is its id, or a label only one slot has. */
export const isSlotDoneForDate = (h: Habit, date: string, ref: string): boolean => {
  const slots = habitSlots(h, date);
  const slot = slots ? resolveSlot(slots, ref) : null;
  return !!slot && completedSlotsForDate(h, date).has(slot.id);
};

/** How many completions count toward `date` (distinct slots when labelled). */
export const habitCountForDate = (h: Habit, date: string): number =>
  isLabeledHabit(h, date) ? completedSlotsForDate(h, date).size : realCompletionsForDate(h, date).length;

/**
 * Whether the day's target has been met — true if the persisted `completedDates`
 * marker is set OR the live count reaches target (the two agree in real data;
 * the OR keeps legacy rows and count habits both correct).
 */
export const isHabitCompleteForDate = (h: Habit, date: string): boolean =>
  h.completedDates.includes(date) || habitCountForDate(h, date) >= habitTarget(h, date);

/** Local YYYY-MM-DD for a Date (no UTC shift). */
const dateKey = (d: Date): string =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

/** Whether the habit's schedule calls for it on `date` (interval-aware). */
export function isHabitScheduledOn(h: Habit, date: Date): boolean {
  const s = h.schedule;
  switch (s.interval) {
    case 'weekly':
      return (s.days ?? []).includes(date.getDay());
    case 'monthly':
      return (s.monthDays ?? []).includes(date.getDate());
    case 'yearly': {
      const mmdd = dateKey(date).slice(5); // MM-DD
      return (s.yearDates ?? []).some((d) => d.slice(5) === mmdd);
    }
    case 'daily':
    default:
      return true;
  }
}

export interface RangeProgress {
  /** Scheduled occurrences in the range. */
  scheduled: number;
  /** Of those, how many met the day's target. */
  completed: number;
  /** completed / scheduled as a 0–100 %. */
  pct: number;
}

/**
 * Completion progress across an inclusive date range.
 *  - Strict: counts only scheduled days (a Mon/Wed habit isn't penalised for Tue).
 *  - Flexible: the schedule is a cadence, so ANY completed day in the range counts
 *    toward the period target — that's what lets the bar progress when you complete
 *    on a non-scheduled day.
 */
export function habitRangeProgress(h: Habit, start: Date, end: Date): RangeProgress {
  const first = new Date(start.getFullYear(), start.getMonth(), start.getDate());
  const last = new Date(end.getFullYear(), end.getMonth(), end.getDate());

  if (!isStrictHabit(h)) {
    let raw = 0;
    const cur = new Date(first);
    while (cur <= last) {
      if (isHabitCompleteForDate(h, dateKey(cur))) raw++;
      cur.setDate(cur.getDate() + 1);
    }
    const total = Math.max(1, habitPeriodTarget(h));
    const completed = Math.min(raw, total);
    return { scheduled: total, completed, pct: Math.round((completed / total) * 100) };
  }

  let scheduled = 0;
  let completed = 0;
  const cur = new Date(first);
  while (cur <= last) {
    if (isHabitScheduledOn(h, cur)) {
      scheduled++;
      if (isHabitCompleteForDate(h, dateKey(cur))) completed++;
    }
    cur.setDate(cur.getDate() + 1);
  }
  return { scheduled, completed, pct: scheduled > 0 ? Math.round((completed / scheduled) * 100) : 0 };
}

/** The habit card's "current period" progress bar: this week (daily/weekly), this month, or this year. */
export function habitCardProgress(h: Habit, today: Date): { label: string; progress: RangeProgress } {
  const y = today.getFullYear();
  const m = today.getMonth();
  const d = today.getDate();
  switch (h.schedule.interval) {
    case 'monthly':
      return { label: 'This month', progress: habitRangeProgress(h, new Date(y, m, 1), new Date(y, m + 1, 0)) };
    case 'yearly':
      return { label: 'This year', progress: habitRangeProgress(h, new Date(y, 0, 1), new Date(y, 11, 31)) };
    default:
      // daily + weekly → this week (Sun … Sat)
      return {
        label: 'This week',
        progress: habitRangeProgress(h, new Date(y, m, d - today.getDay()), new Date(y, m, d - today.getDay() + 6)),
      };
  }
}

/* ── Day-state model (exact-date vs flexible scheduling) ────────────────────── */

/** Per-day state in the habit calendar. */
export type HabitDayState = 'none' | 'open' | 'done' | 'missed';

/** Exact-date (strict) is the default; only `exactDate === false` is flexible. */
export const isStrictHabit = (h: Habit): boolean => h.exactDate !== false;

/** Obligations per period (week/month/year), or 1/day for daily. */
export const habitPeriodTarget = (h: Habit): number => {
  switch (h.schedule.interval) {
    case 'weekly':
      return Math.max(1, h.schedule.days?.length ?? 0);
    case 'monthly':
      return Math.max(1, h.schedule.monthDays?.length ?? 0);
    case 'yearly':
      return Math.max(1, h.schedule.yearDates?.length ?? 0);
    default:
      return 1;
  }
};

/** Inclusive [start,end] of the period (week=Sun→Sat / month / year / day) holding `date`. */
function periodBounds(h: Habit, date: Date): [Date, Date] {
  const y = date.getFullYear();
  const m = date.getMonth();
  const d = date.getDate();
  switch (h.schedule.interval) {
    case 'weekly':
      return [new Date(y, m, d - date.getDay()), new Date(y, m, d - date.getDay() + 6)];
    case 'monthly':
      return [new Date(y, m, 1), new Date(y, m + 1, 0)];
    case 'yearly':
      return [new Date(y, 0, 1), new Date(y, 11, 31)];
    default:
      return [new Date(y, m, d), new Date(y, m, d)];
  }
}

/** How many days in `date`'s period already met target (flexible-mode bookkeeping). */
export function habitPeriodCompletedCount(h: Habit, date: Date): number {
  const [start, end] = periodBounds(h, date);
  let n = 0;
  const cur = new Date(start);
  while (cur <= end) {
    if (isHabitCompleteForDate(h, dateKey(cur))) n++;
    cur.setDate(cur.getDate() + 1);
  }
  return n;
}

/**
 * The calendar state for a habit on a given day:
 *  - done   → target met that day
 *  - open   → completable TODAY (strict: scheduled; flexible: period unmet)
 *  - missed → strict only: a scheduled day that passed without completion (locked)
 *  - none   → no obligation, OR a FUTURE day (you can't complete ahead of time)
 *
 * Future days are always locked: a habit can only be completed on the day itself
 * (or backfilled into the past), never in advance — daily habits included.
 */
export function habitDayState(h: Habit, date: Date, today: Date): HabitDayState {
  const ds = dateKey(date);
  const td = dateKey(today);
  if (isHabitCompleteForDate(h, ds)) return 'done';
  if (ds > td) return 'none'; // future → locked (can't complete ahead of time)
  const isPast = ds < td;
  if (isStrictHabit(h)) {
    if (!isHabitScheduledOn(h, date)) return 'none';
    return isPast ? 'missed' : 'open';
  }
  if (habitPeriodCompletedCount(h, date) >= habitPeriodTarget(h)) return 'none';
  return isPast ? 'none' : 'open';
}

/** Whether a click on this day may change completion (the click-gating fix). */
export function canCompleteHabitOn(h: Habit, date: Date, today: Date): boolean {
  if (dateKey(date) > dateKey(today)) return false; // future is always locked
  const st = habitDayState(h, date, today);
  if (st === 'open' || st === 'done') return true;
  if (st === 'missed') return !!h.allowBackfill;
  return false; // 'none' is locked
}

/** Streak → points multiplier (single source of truth, matches habitStore). */
export const habitStreakMultiplier = (streak: number): number => {
  if (streak >= 90) return 3.0;
  if (streak >= 60) return 2.0;
  if (streak >= 30) return 1.6;
  if (streak >= 14) return 1.4;
  if (streak >= 7) return 1.2;
  if (streak >= 3) return 1.1;
  return 1.0;
};

const DAY_ABBR = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

/**
 * A habit's schedule in words ("Daily", "Weekly · Mon, Thu"). Moved here from
 * `HabitSummaryPanel` (2026-09-30) so Mind's care practices show a linked
 * habit's schedule with the SAME words the habit page uses — a second copy is
 * how two pages end up describing one schedule differently.
 */
export function habitScheduleSummary(habit: Pick<Habit, 'schedule'>): string {
  const s = habit.schedule;
  switch (s.interval) {
    case 'weekly':
      return s.days?.length ? `Weekly · ${s.days.map((d) => DAY_ABBR[d]).join(', ')}` : 'Weekly · no days picked';
    case 'monthly':
      return s.monthDays?.length ? `Monthly · day ${s.monthDays.join(', ')}` : 'Monthly · no days picked';
    case 'yearly':
      return s.yearDates?.length ? `Yearly · ${s.yearDates.length} date${s.yearDates.length > 1 ? 's' : ''}` : 'Yearly · no dates picked';
    case 'daily':
    default:
      return 'Daily';
  }
}
